import React, { useEffect } from "react";
import {
  Button,
  Container,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Box,
  Card,
} from "@mui/material";
import RegisterHeader from "./Common/Header";
import RegisterSteps from "./Common/Steps";
import { useNavigate } from "react-router-dom";
import { useRegister } from "../../context/RegisterContext";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Carwash } from "../../types/RegisterType";
import { REQUIRED_FIELD } from "../../utils/constants";
import CnpjMaskComponent from "../InputMask/CnpjMask";
import { cnpjValidator } from "../../utils/cnpj-validator";
import PhoneMask from "../InputMask/PhoneMask";
import Footer from "../Footer";
import ReactGA from "react-ga4";

const RegisterCarwash = () => {
  const navigate = useNavigate();
  const { registerCarwash, getRegisterData, canProceed } = useRegister();
  
  const steps = [
    'Endereço',
    'Responsável',
    'Negócio',
    'Explicação',
    'Categoria',
    'Login',
    'Confirmação'
  ];

  const schemaCarwash = yup.object({
    cnpj: yup
      .string()
      .required(REQUIRED_FIELD)
      .test("cpf", "CNPJ Inválido", cnpjValidator),
    car_wash_name: yup.string().required(REQUIRED_FIELD),
    phone: yup.string().required(REQUIRED_FIELD),
    company_name: yup.string().required(REQUIRED_FIELD),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schemaCarwash),
  });

  const onSubmitCarwash = (data: Carwash) => {
    registerCarwash(data);
    navigate("/register/explanation");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!canProceed()) {
      navigate("/parceiros");
      return;
    }

    const { carwash } = getRegisterData();
    setValue("cnpj", carwash?.cnpj as string);
    setValue("car_wash_name", carwash?.car_wash_name as string);
    setValue("company_name", carwash?.company_name as string);
    setValue("phone", carwash?.phone as string);
  }, []);

  return (
    <div className="Register" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
    }}>
      <RegisterHeader />

      <Container maxWidth="md" className="MainContainer">
        <RegisterSteps currentStep={3} steps={steps} />
        
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h1" sx={{ 
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 'bold',
            color: '#0f172a',
            marginBottom: 2
          }}>
            Informações do lava-rápido
          </Typography>
          <Typography variant="h5" sx={{ 
            color: '#64748b',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Preencha com os dados do seu negócio
          </Typography>
        </Box>

        <Card sx={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          backgroundColor: 'white'
        }}>
          <form onSubmit={handleSubmit(onSubmitCarwash)}>
            <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="cnpj"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="cnpj">CNPJ</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="cnpj"
                        type="text"
                        placeholder="00.000.000/0000-00"
                        fullWidth
                        error={!!errors.cnpj}
                        inputComponent={CnpjMaskComponent as any}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.cnpj?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="car_wash_name"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="car_wash_name">
                        Nome da loja (como aparecerá no app)
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="car_wash_name"
                        type="text"
                        placeholder="Exemplo: Lava Já"
                        fullWidth
                        error={!!errors.car_wash_name}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.car_wash_name?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="company_name"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="company_name">
                        Razão Social
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="company_name"
                        type="text"
                        placeholder=""
                        fullWidth
                        error={!!errors.company_name}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.company_name?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="phone">
                        Telefone ou celular da loja (como aparecerá no app)
                      </InputLabel>
                      <OutlinedInput
                        {...field}
                        id="phone"
                        type="text"
                        placeholder="Exemplo: 11  99999-0000"
                        fullWidth
                        error={!!errors.phone}
                        inputComponent={PhoneMask as any}
                      />

                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.phone?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate("/register/owner")}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderColor: '#d1d5db',
                    color: '#374151',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                      borderColor: '#9ca3af',
                    },
                  }}
                >
                  Voltar
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  onClick={() => {
                    ReactGA.event("continue_carwash_click", {
                      event: 'continue_carwash_click',
                      button_id: 'continue_carwash',
                      page_path: window.location.pathname
                    })
                  }}
                  sx={{
                    padding: '12px 24px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                  }}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Card>
        
        <Box sx={{ textAlign: 'center', marginTop: 4, marginBottom: 2 }}>
          <Typography variant="body2" sx={{ 
            color: '#64748b',
            fontSize: '1rem'
          }}>
            Ficou alguma dúvida?{" "}
            <a 
              href="/fale-conosco" 
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Fale Conosco
            </a>
          </Typography>
        </Box>
      </Container>

      <Footer/>
    </div>
  );
};

export default RegisterCarwash;
